import React, { useState, useEffect, useRef, useMemo } from 'react';
import '../Styles/globals.css';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useUserProfile } from '../hooks/useUserProfile';
import LookalLogo from '../Images/Lookal Logo.png';
import { ADMIN_SIDEBAR_ITEMS } from '../constants/adminSidebar';
import { useAdminLoading } from '../contexts/AdminLoadingContext';
import { supabase } from '../lib/supabase';

interface SidebarItem {
  key: string;
  label: string;
  icon: React.ReactNode;
  special?: boolean;
}

/**
 * Handle for the navigation watchdog timer. `window.setTimeout` is used (rather
 * than the Node typing that `setTimeout` resolves to here) so the handle is a
 * plain number the DOM `clearTimeout` accepts.
 */
type TimerHandle = number | undefined;

// Icon mapping for sidebar items
const getIconForItem = (key: string): React.ReactNode => {
  switch (key) {
    case 'dashboard':
      return <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg>;
    case 'nodes':
      return <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>;
    case 'ads':
      return <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>;
    case 'lookal-internal-ads':
      return <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a2 2 0 012-2h12a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm4 4h8M8 13h5m-5 4h8" /></svg>;
    case 'campaign-approval':
      return <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;
    case 'qrcodes':
      return <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" /></svg>;
    case 'notifications':
      return <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" /></svg>;
    case 'smart-tips':
      return <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" /></svg>;
    case 'credit-bonus':
      return <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;
    case 'slot-tiers':
      return <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" /></svg>;
    case 'configuration':
      return <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>;
    case 'reports':
      return <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>;
    case 'logs':
      return <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>;
    case 'tv-display-logs':
      return <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>;
    case 'settings':
      return <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" /></svg>;
    case 'licensing-approvals':
      return <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;
    case 'licensing-monitor':
      return <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>;
    case 'payout-review':
      return <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" /></svg>;
    case 'settlement-batches':
      return <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v4a2 2 0 002 2h2a2 2 0 002-2V7m-6 0a2 2 0 012-2h2a2 2 0 012 2m-6 0h6m0 0a2 2 0 012-2h2a2 2 0 012 2m-6 0h6m2 4v4a2 2 0 01-2 2h-2a2 2 0 01-2-2v-4m6 0h-6m-2 0V7m0 0h4" /></svg>;
    case 'ota-updates':
      return <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>;
    case 'roles-permissions':
      return <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" /></svg>;
    case 'agreement-templates':
      return <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>;
    case 'ml-invitations':
      return <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>;
    default:
      return <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;
  }
};

const SIDEBAR_ITEMS: SidebarItem[] = ADMIN_SIDEBAR_ITEMS.map(item => ({
  key: item.key,
  label: item.label,
  icon: getIconForItem(item.key)
}));

interface AdminLayoutProps {
  children: React.ReactNode;
  currentPage?: string;
  onLogout?: () => void;
}

// Loading spinner component
const LoadingSpinner: React.FC<{ message?: string }> = ({ message = "Loading..." }) => (
  <div className="flex items-center justify-center h-64">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
      <p className="mt-4 text-gray-600">{message}</p>
    </div>
  </div>
);

const AdminLayout: React.FC<AdminLayoutProps> = ({ children, currentPage, onLogout }) => {
  const { profile } = useUserProfile();
  const { user, isAdmin = false, adminPermissions = [] } = useAuth();
  const { isLoading: contextLoading, loadingMessage } = useAdminLoading();
  const [showProfile, setShowProfile] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    // Load collapsed state from localStorage
    const saved = localStorage.getItem('adminSidebarCollapsed');
    return saved === 'true';
  });
  // Below lg the sidebar becomes an off-canvas drawer: a fixed 256px rail plus
  // ml-64 on the content left ~100px of usable width on a 390px phone.
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const [currentRoute, setCurrentRoute] = useState('');
  const [pendingApprovalCount, setPendingApprovalCount] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const navigationTimeoutRef = useRef<TimerHandle>(undefined);

  // Superadmins (is_admin) see every section; role-based admins only see the
  // sections granted to their role (permission key == sidebar item key).
  const visibleItems = useMemo(() => {
    if (isAdmin) return SIDEBAR_ITEMS;
    return SIDEBAR_ITEMS.filter((item) => adminPermissions.includes(item.key));
  }, [isAdmin, adminPermissions]);

  // Fetch pending campaign approval count for the sidebar badge
  useEffect(() => {
    let cancelled = false;
    const fetchPendingCount = async () => {
      const { count, error } = await supabase
        .from('campaigns')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'Pending');
      if (!cancelled && !error) {
        setPendingApprovalCount(count ?? 0);
      }
    };
    void fetchPendingCount();
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  // Save collapsed state to localStorage whenever it changes
  const toggleCollapsed = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    localStorage.setItem('adminSidebarCollapsed', newState.toString());
  };

  const handleSidebarClick = (key: string) => {
    // Prevent multiple rapid clicks
    if (isNavigating) return;

    setShowProfile(false);
    setMobileNavOpen(false);
    // Find the item in ADMIN_SIDEBAR_ITEMS and use its route
    const item = ADMIN_SIDEBAR_ITEMS.find(item => item.key === key);
    const targetRoute = item ? item.route : `/admin/${key}`;

    // Only navigate if it's a different route
    if (targetRoute !== location.pathname) {
      setIsNavigating(true);
      setCurrentRoute(targetRoute);

      // Clear any existing timeout
      window.clearTimeout(navigationTimeoutRef.current);

      // Auto-hide loading state after reasonable time
      navigationTimeoutRef.current = window.setTimeout(() => {
        setIsNavigating(false);
      }, 3000);

      navigate(targetRoute);
    }
  };

  // Track route changes to manage loading state
  useEffect(() => {
    if (currentRoute !== location.pathname) {
      setIsNavigating(false);
      setCurrentRoute(location.pathname);
      window.clearTimeout(navigationTimeoutRef.current);
    }
    setMobileNavOpen(false);
  }, [location.pathname, currentRoute]);

  // Lock body scroll and close on Escape while the drawer is open.
  useEffect(() => {
    if (!mobileNavOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileNavOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [mobileNavOpen]);

  const handleProfile = () => {
    setShowProfile(true);
  };

  const handleLogout = () => {
    setIsNavigating(true);
    if (onLogout) onLogout();
    navigate('/auth');
  };

  return (
    <div className="min-h-screen flex">
      {/* Mobile top bar: the drawer trigger plus the current section name. */}
      <header className="lg:hidden fixed top-0 inset-x-0 z-30 flex items-center gap-3 bg-white border-b px-4 py-3">
        <button
          type="button"
          onClick={() => setMobileNavOpen(true)}
          aria-label="Open admin menu"
          aria-expanded={mobileNavOpen}
          aria-controls="admin-sidebar"
          className="p-2 -ml-2 rounded-lg text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
        <img src={LookalLogo} alt="LooKal" className="h-8 w-auto" />
      </header>

      {/* Backdrop for the mobile drawer */}
      {mobileNavOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/40 z-30"
          onClick={() => setMobileNavOpen(false)}
          aria-hidden="true"
        />
      )}

      <aside
        id="admin-sidebar"
        className={`bg-white border-r flex flex-col transition-transform lg:transition-all duration-300 fixed h-screen z-40 lg:z-10 w-64 ${
          mobileNavOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0 ${isCollapsed ? 'lg:w-20' : 'lg:w-64'}`}
      >
        <div className="flex flex-col h-full">
          <div className="lg:hidden flex items-center justify-end px-4 py-3 border-b">
            <button
              type="button"
              onClick={() => setMobileNavOpen(false)}
              aria-label="Close admin menu"
              className="p-2 -mr-2 rounded-lg text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <button
            onClick={toggleCollapsed}
            className="hidden lg:block absolute -right-3 top-6 bg-white border rounded-full p-1 hover:bg-gray-100 z-10 shadow-md"
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <svg
              className={`w-4 h-4 text-gray-600 transition-transform duration-300 ${isCollapsed ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="mb-8 flex items-center justify-center pt-6 px-6">
            <img
              src={LookalLogo}
              alt="LooKal"
              className={`transition-all duration-300 object-contain ${isCollapsed ? 'lg:h-8 lg:w-8 h-12 w-auto' : 'h-12 w-auto'}`}
            />
          </div>
          <nav className="flex-1 overflow-y-auto px-6">
            <ul className="space-y-2">
              {visibleItems.map(item => (
                <li
                  key={item.key}
                  className={`relative flex items-center rounded py-2 cursor-pointer transition-colors ${
                    isCollapsed ? 'lg:justify-center lg:px-0 gap-3 px-3' : 'gap-3 px-3'
                  } ${
                    location.pathname === `/admin/${item.key}`
                      ? 'font-semibold text-black bg-gray-100'
                      : 'text-gray-600 hover:text-black hover:bg-gray-50'
                  }`}
                  onClick={() => handleSidebarClick(item.key)}
                  title={isCollapsed ? item.label : ''}
                >
                  {item.icon}
                  <span className={isCollapsed ? 'lg:hidden' : ''}>{item.label}</span>
                  {item.key === 'campaign-approval' && pendingApprovalCount > 0 && (
                    <span
                      className={`ml-auto inline-flex items-center justify-center rounded-full bg-red-600 text-white text-xs font-bold min-w-[20px] h-5 px-1.5 ${
                        isCollapsed ? 'lg:absolute lg:-top-1 lg:-right-1' : ''
                      }`}
                      title={`${pendingApprovalCount} campaign${pendingApprovalCount !== 1 ? 's' : ''} pending approval`}
                    >
                      {pendingApprovalCount > 99 ? '99+' : pendingApprovalCount}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </nav>
          <div className="mt-auto border-t pt-4 pb-6 px-6 space-y-2 bg-white">
            <button
              className={`w-full bg-blue-600 text-white font-bold py-3 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 ${isCollapsed ? 'lg:px-2' : 'px-4'}`}
              onClick={handleProfile}
              title={isCollapsed ? 'Profile' : ''}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <span className={isCollapsed ? 'lg:hidden' : ''}>Profile</span>
            </button>
            <button
              className={`w-full bg-red-600 text-white font-bold py-3 rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center gap-2 ${isCollapsed ? 'lg:px-2' : 'px-4'}`}
              onClick={handleLogout}
              title={isCollapsed ? 'Logout' : ''}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              <span className={isCollapsed ? 'lg:hidden' : ''}>Logout</span>
            </button>
          </div>
        </div>
      </aside>
      <main
        className={`flex-1 bg-gray-50 p-4 sm:p-6 lg:p-10 pt-20 lg:pt-10 w-full min-w-0 ${
          isCollapsed ? 'lg:ml-20' : 'lg:ml-64'
        } transition-all duration-300`}
      >
          {showProfile ? (
            <div className="bg-white rounded-xl shadow p-8 max-w-lg mx-auto">
              <h2 className="text-xl font-bold mb-4">Admin Profile</h2>
              <div className="mb-2">Name: <span className="font-semibold">{profile?.name || user?.email?.split('@')[0] || 'Admin User'}</span></div>
              <div className="mb-2">Email: <span className="font-semibold">{profile?.email || user?.email || 'No email provided'}</span></div>
              <div className="mb-2">Role: <span className="font-semibold">{profile?.is_admin ? 'Administrator' : 'User'}</span></div>
              {profile?.business_name && (
                <div className="mb-2">Business: <span className="font-semibold">{profile.business_name}</span></div>
              )}
              {profile?.phone && (
                <div className="mb-2">Phone: <span className="font-semibold">{profile.phone}</span></div>
              )}
              <button className="mt-4 px-4 py-2 bg-blue-600 text-white rounded" onClick={() => setShowProfile(false)}>Back</button>
            </div>
          ) : isNavigating || contextLoading ? (
            <div className="bg-white rounded-xl shadow-sm p-8">
              <LoadingSpinner message={contextLoading ? loadingMessage : "Loading page..."} />
            </div>
          ) : (
            <div className="animate-fade-in">
              {children}
            </div>
          )}
        </main>
    </div>
  );
};

export default AdminLayout;
